import { useState } from "react"
import useAccountEnhanced from "../../../hooks/use-account-enhanced";
import { itaQueryOptions, itaMutations } from "src/api/mvte/ita-query-options";
import { Card } from "src/components/card";
import { useQuery } from "@tanstack/react-query";
import { TaskerWorkRole } from "../types/WorkRole";
import { GrantAccessPayload } from "../types/RoleAccessPayload";

const inputClass =
"w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-[#0B1D3A] focus:outline-none focus:ring-1 focus:ring-[#0B1D3A]";

const labelClass = "mb-1 block text-sm font-medium text-gray-900";

const primaryButtonClass =
"cursor-pointer rounded-md bg-[#0078d4] px-4 py-2 text-sm font-medium text-white hover:bg-[#0078d4]/90 disabled:cursor-not-allowed disabled:opacity-50";

function GrantAccess()
{
const accountEnhanced = useAccountEnhanced();
const [employeeCodeInput, setEmployeeCodeInput] = useState("")
const [submittedPersonCode, setSubmittedPersonCode] = useState("")
const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null)
const [roleDropdownOpen, setRoleDropdownOpen] = useState(false)
const [hoveredRoleId, setHoveredRoleId] = useState<number | null>(null)
const [note, setNote] = useState("Hardcoded for now")
const ALLOWED_ROLE_NAMES = ["Safety Compliance Specialist", "Safety Compliance Manager"];

const taskerRoleUpdateMutation = itaMutations.useUpdateTaskerPersonRole();

const roleQuery = useQuery({
  ...itaQueryOptions.taskerWorkRoleQueryOptions(),
  enabled: !!submittedPersonCode,
});

const personInfoQuery = useQuery({
  ...itaQueryOptions.taskerPersonInfoQueryOptions({ params: { personCode: submittedPersonCode } }),
  enabled: !!submittedPersonCode,
});

const personInfo = personInfoQuery.data?.PersonInfo ?? null;
const currentRoles = personInfoQuery.data?.RoleInfo ?? [];

const availableRoles: TaskerWorkRole[] = (roleQuery.data ?? []).filter(
 (role) =>
   role.Active &&
   ALLOWED_ROLE_NAMES.includes(role.Role) &&
   !currentRoles.some((r) => r.Role === role.Role)
);

const selectedRole = availableRoles.find((r) => r.RoleId === selectedRoleId) ?? null;

const searchState: "idle" | "loading" | "found" | "notfound" | "error" =
  !submittedPersonCode
    ? "idle"
    : personInfoQuery.isPending
    ? "loading"
    : personInfoQuery.isError
    ? "error"
    : personInfo
    ? "found"
    : "notfound";

function handleSubmit(event: React.FormEvent<HTMLFormElement>)
{
  event.preventDefault();
  const code = employeeCodeInput.trim();
  if (!code) return;
  setSelectedRoleId(null);
  setRoleDropdownOpen(false);
  setSubmittedPersonCode(code);
}

function handleSelectRole(roleId: number)
{
  setSelectedRoleId(roleId);
  setRoleDropdownOpen(false);
  setHoveredRoleId(null);
}

function handleClick()
{
  if (!selectedRole || !personInfo) return;

  const payload: GrantAccessPayload =
  {
    EmployeeCode: submittedPersonCode,
    RoleId: selectedRole.RoleId,
    ChangedBy: accountEnhanced.me.employeeCode,
    SourceSystem: "MVTE-Center",
    Note: note,
  };

  taskerRoleUpdateMutation.mutate({payload}, {
    onSuccess: () => {
      personInfoQuery.refetch();
      setSelectedRoleId(null);
      setNote("");
    },
  });
}

return(
  <>
    <div className="mb-4">
      <h1 className="text-3xl font-semibold text-gray-200">Grant Safety Compliance Tool</h1>
      <hr className="mt-2 border-t border-gray-200" />
    </div>

    <Card>
      <div className="p-6">
        <form onSubmit={handleSubmit}>
          <label htmlFor="EmployeeCode" className={labelClass}>Employee Code:</label>
          <div className="mt-1.5 flex gap-2">
            <input type="text" id="EmployeeCode" name="EmployeeCode" placeholder="LASFI" autoComplete="off" maxLength={25}
              value={employeeCodeInput} onChange={(e) => setEmployeeCodeInput(e.target.value)} className={inputClass}/>
            <button type="submit" className={primaryButtonClass} disabled={searchState === "loading"}>
              {searchState === "loading" ? "Searching…" : "Search"}
            </button>
          </div>
        </form>

        {
          searchState === "notfound" && (
            <div className="mt-4 rounded-md bg-red-50 px-3 py-2 text-sm font-medium text-red-700">User not found.</div>
          )
        }
        {
          searchState === "error" && (
            <div className="mt-4 rounded-md bg-red-50 px-3 py-2 text-sm font-medium text-red-700">
              Something went wrong looking up that user. Please try again.
            </div>
          )
        }
        {
          searchState === "found" && personInfo &&
          (
            <div className="mt-6">
              <h2 className="mb-2 text-lg font-semibold text-gray-900">User Information</h2>
              <div className="mb-5 overflow-hidden rounded-lg border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">First</th>
                      <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Last</th>
                      <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Title</th>
                      <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Active</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    <tr>
                      <td className="px-4 py-2 text-sm text-gray-900">{personInfo.FirstName}</td>
                      <td className="px-4 py-2 text-sm text-gray-900">{personInfo.LastName}</td>
                      <td className="px-4 py-2 text-sm text-gray-900">{personInfo.JobTitle}</td>
                      <td className="px-4 py-2 text-sm text-gray-900">{personInfo.Active ? "True" : "False"}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="mb-5">
                <p className={`${labelClass} mb-1.5`}>Current Roles:</p>
                {
                  currentRoles.length > 0 ?
                  (
                    <ol className="m-0 list-decimal space-y-1 pl-5 text-sm leading-relaxed text-gray-700">
                      {currentRoles.map((role) => (<li key={role.PersonRoleRelationship}>{role.Role}</li>))}
                    </ol>
                  ) : (
                    <p className="m-0 text-sm text-gray-500">No roles assigned.</p>
                  )
                }
              </div>

              <label htmlFor="Role" className={labelClass}>Role:</label>
              <div className="relative mt-1.5 inline-block">
                <button
                  type="button"
                  id="Role"
                  onClick={() => setRoleDropdownOpen((open) => !open)}
                  disabled={roleQuery.isPending}
                  className="flex min-w-[260px] cursor-pointer items-center justify-between gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-[#0B1D3A] focus:outline-none focus:ring-1 focus:ring-[#0B1D3A] disabled:cursor-not-allowed disabled:opacity-50">
                  <span className={selectedRole ? "text-gray-900" : "text-gray-400"}>
                    {roleQuery.isPending
                      ? "Loading roles…"
                      : selectedRole?.Role ?? "----Select a Role----"}
                  </span>
                  <span className="text-[10px] text-gray-500">▾</span>
                </button>
                {
                  roleQuery.isError && (
                    <p className="mt-1 text-xs text-red-600">Couldn't load roles. Try again.</p>
                  )
                }
                {
                  roleDropdownOpen &&
                  (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setRoleDropdownOpen(false)}/>
                      <ul className="absolute left-0 top-[calc(100%+4px)] z-20 m-0 max-h-[280px] min-w-[260px] list-none overflow-y-auto rounded-md border border-gray-300 bg-white p-1 shadow-lg">
                      {
                        availableRoles.map((role) =>
                        (
                          <li key={role.RoleId}
                          onMouseEnter={() => setHoveredRoleId(role.RoleId)}
                          onMouseLeave={() => setHoveredRoleId(null)}
                          onClick={() => handleSelectRole(role.RoleId)}
                          className={`cursor-pointer rounded px-3 py-2 text-sm text-gray-900 ${
                            hoveredRoleId === role.RoleId ? "bg-blue-50" : "bg-white"
                          }`}>
                            {role.Role}
                          </li>
                        ))
                      }
                      </ul>
                      {
                        hoveredRoleId !== null &&
                        (
                          <div className="pointer-events-none absolute left-[calc(100%+8px)] top-[44px] z-30 w-60 rounded-md bg-gray-900 px-3 py-2 text-xs font-normal leading-snug text-white shadow-lg">
                            {availableRoles.find((r) => r.RoleId === hoveredRoleId)?.RoleDescription}
                          </div>
                        )
                      }
                    </>
                  )
                }
              </div>

              <div className="mt-4">
                <button
                  type="button"
                  className={primaryButtonClass}
                  onClick={handleClick}
                  disabled={!selectedRole || taskerRoleUpdateMutation.isPending}
                >
                  {taskerRoleUpdateMutation.isPending ? "Updating…" : "Update Role"}
                </button>
                {
                  taskerRoleUpdateMutation.isError && (
                    <p className="mt-2 text-sm font-medium text-red-700">Failed to update role. Please try again.</p>
                  )
                }
              </div>
            </div>
          )
        }
      </div>
    </Card>
  </>
)
}
export { GrantAccess }